/**
 * calendarInit.js
 *
 * ES6 Module bootstrapping the FullCalendar-based application.
 *
 * Responsibilities:
 * - Initializes CalendarApplication with bounded retry logic.
 * - Skips initialization on supervision/info pages or when disabled.
 * - Sets up backward compatibility and global event dispatch.
 * - Hooks global error handlers and provides a cleanup routine.
 *
 * Notes:
 * - Assumes browser environment with DOM and window.
 */

import CalendarApplication from './core/CalendarApplication.js';
import ErrorHandler from '../utils/ErrorHandler.js';
import toggleHandler from './handlers/calendarViewToggle.js';
import filterBarHandler from './handlers/filterBarHandler.js';
import { initPaginationHandler } from './pagination-handler.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

function escapeHtml(str = '') {
  return String(str)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function readServerEventData() {
  // Optional server hydration: <script id="calendar-events-json" type="application/json">...</script>
  const el = document.getElementById('calendar-events-json');
  if (!el) return null;

  try {
    const json = el.textContent || '';
    if (!json.trim()) return [];
    return JSON.parse(json);
  } catch (e) {
    // Don’t hard-fail init; just log and return null.
    console.warn('Failed to parse calendar-events-json:', e);
    return null;
  }
}

class CalendarInitializer {
  constructor() {
    this.errorHandler = new ErrorHandler({ module: 'CalendarInitializer', maxErrors: 25 });

    this.calendarAppInstance = null;
    this.isInitialized = false;

    this.initializationAttempts = 0;
    this.maxRetryAttempts = 3;
    this.retryBaseDelayMs = 250; // exponential backoff base
  }

  isSupervisionOrInfoPage() {
    const selectors = [
      '.supervision-events',
      '.csat-info',
      '.apsats-info',
      '.cptt-info',
      '.mentoring-info',
      '.disclosure-guide-info'
    ];
    if (selectors.some(sel => document.querySelector(sel))) return true;

    const paths = ['/supervision/', '/csat', '/apsats', '/cptt', '/mentoring', '/disclosure-guide'];
    return paths.some(p => window.location.pathname.includes(p));
  }

  shouldSkipInitialization() {
    if (!isBrowser) return true;
    if (window.skipCalendarInit) return true;
    if (this.isSupervisionOrInfoPage()) return true;

    const calendarNode = document.querySelector('#calendar');
    const isCalendarPage =
      window.location.pathname.includes('/calendar') ||
      Boolean(document.querySelector('.calendar-calendar, .calendar-list'));

    // If calendar is injected later, we currently skip. This matches your existing behavior.
    return !calendarNode && !isCalendarPage;
  }

  async initializeCalendar() {
    if (this.shouldSkipInitialization()) return true;
    if (this.calendarAppInstance?.state?.initialized) return true;

    const debug = Boolean(window.calendarDebug);

    for (let attempt = 1; attempt <= this.maxRetryAttempts; attempt++) {
      this.initializationAttempts = attempt;

      try {
        const app = new CalendarApplication({
          debug,
          exposeGlobals: true,
          autoRender: true,
          errorRetryAttempts: this.maxRetryAttempts,
          componentTimeout: 5000
        });

        // Optional: pass server events if your CalendarApplication supports it.
        // If it doesn't, remove this line or adapt to your app's API.
        const serverEvents = readServerEventData();
        if (serverEvents) {
          app.initialEvents = serverEvents; // soft convention; adjust if your app uses a different field
        }

        const success = await app.initialize();

        if (success) {
          this.calendarAppInstance = app;
          this.isInitialized = true;
          window.__calendarInitialized = true;

          this.initializeViewToggle();
          this.initializeFilterBar();
          this.initializePagination();

          this.dispatchInitializedEvent(app);

          this.errorHandler.logSuccess('Calendar initialized', { attempts: attempt });
          return true;
        }

        this.errorHandler.handleError('Initialization returned false', null, { attempt });
      } catch (err) {
        if (attempt >= this.maxRetryAttempts) {
          this.errorHandler.handleFatalError('Fatal init error', err, { attempt });
          this.showErrorMessage('Calendar Error', 'An unexpected error occurred.', err?.message);
          return false;
        }
        this.errorHandler.handleError('Init attempt failed', err, { attempt });
      }

      if (attempt < this.maxRetryAttempts) {
        const delay = this.retryBaseDelayMs * 2 ** (attempt - 1);
        await new Promise(res => setTimeout(res, delay));
      }
    }

    this.showErrorMessage('Calendar Unavailable', 'Could not load calendar.');
    return false;
  }

  initializeViewToggle() {
    try {
      if (!isBrowser) return;

      if (toggleHandler && typeof toggleHandler.initialize === 'function') {
        const ok = toggleHandler.initialize();
        if (ok) {
          this.errorHandler.logInfo('View toggle handler initialized');
          // Expose for other modules that read it (e.g., filter bar)
          window.toggleHandler = toggleHandler;
        } else {
          this.errorHandler.logInfo('View toggle handler initialization returned false');
        }
      } else {
        this.errorHandler.logInfo('View toggle handler not available');
      }
    } catch (err) {
      this.errorHandler.handleError('View toggle initialization error', err);
    }
  }

  initializeFilterBar() {
    try {
      if (!isBrowser) return;

      if (filterBarHandler && typeof filterBarHandler.initialize === 'function') {
        filterBarHandler.initialize({ debug: Boolean(window.calendarDebug) });
        this.errorHandler.logInfo('Filter bar handler initialized');
      }
    } catch (err) {
      this.errorHandler.handleError('Filter bar initialization error', err);
    }
  }

  initializePagination() {
    try {
      if (!isBrowser) return;

      if (initPaginationHandler && typeof initPaginationHandler === 'function') {
        initPaginationHandler();
        this.errorHandler.logInfo('Pagination handler initialized');
      }
    } catch (err) {
      this.errorHandler.handleError('Pagination initialization error', err);
    }
  }

  dispatchInitializedEvent(app) {
    try {
      const event = new CustomEvent('calendarInitialized', {
        detail: {
          app,
          calendar: app.state?.components?.get?.('calendar'),
          eventData: app.state?.components?.get?.('eventData'),
          modal: app.state?.components?.get?.('modal'),
          renderer: app.state?.components?.get?.('renderer')
        }
      });
      document.dispatchEvent(event);
      this.errorHandler.logInfo('Initialization event dispatched');
    } catch (e) {
      this.errorHandler.handleError('Event dispatch failed', e);
    }
  }

  showErrorMessage(title, message, technical = '') {
    const el = document.querySelector('#calendar');
    if (!el) return;

    const tech = technical ? escapeHtml(technical) : '';
    el.innerHTML = `
      <div style="padding:20px;text-align:center;color:${tech ? '#d32f2f' : '#666'};">
        <h3>${escapeHtml(title)}</h3>
        <p>${escapeHtml(message)}</p>
        ${tech ? `<details><summary>Details</summary><pre>${tech}</pre></details>` : ''}
      </div>
    `;

    this.errorHandler.logError('Error message shown', { title });
  }

  cleanup() {
    try {
      toggleHandler?.destroy?.();
    } catch { }
    try {
      this.calendarAppInstance?.destroy?.();
    } catch { }
    this.errorHandler.logInfo('Cleanup done');
  }

  handleGlobalError(event) {
    if (String(event?.filename || '').includes('calendar') || document.querySelector('#calendar')) {
      this.errorHandler.handleFatalError('Unhandled JS error', event?.error || event);
    }
  }

  handlePromiseRejection(event) {
    if (document.querySelector('#calendar')) {
      this.errorHandler.handleFatalError('Unhandled promise rejection', event?.reason || event);
    }
  }

  getStats() {
    const errs = this.errorHandler.getErrorStats();
    return { initialized: this.isInitialized, errors: errs };
  }
}

const initializer = new CalendarInitializer();

if (isBrowser) {
  document.addEventListener('DOMContentLoaded', () => initializer.initializeCalendar(), { once: true });
  window.addEventListener('beforeunload', () => initializer.cleanup());
  window.addEventListener('error', e => initializer.handleGlobalError(e));
  window.addEventListener('unhandledrejection', e => initializer.handlePromiseRejection(e));
}

export default initializer;
export { CalendarInitializer };
